/**
 * RegularUserShell — single shell for every signed-in user.
 *
 * Nav items rendered for everyone:
 *   Home / My Vault / Explore / Verify / Settings
 *
 * If `useIsAdmin()` returns true, an additional **Advanced** dropdown
 * appears with: Spark / Conductor / Attestations / Coach / Connectors
 * / Command / OMEGA / CEO Dashboard. The dropdown is hidden completely
 * for non-admin viewers — they cannot see admin nav even if they
 * inspect the rendered DOM.
 *
 * Responsive: desktop top bar (md+); mobile hamburger drawer that
 * lists all nav items grouped by section.
 */

import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Home as HomeIcon,
  LogOut,
  Menu as MenuIcon,
  Plug,
  Settings as SettingsIcon,
  ShieldAlert,
  ShoppingBag,
  Sparkles,
  Vault as VaultIcon,
  X,
} from 'lucide-react';

import { FounderSignBar } from '@/components/FounderSignBar';
import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { OfflineBanner } from '@/components/OfflineBanner';
import { Toaster } from '@/components/Toaster';
import { useAuthStore } from '@/stores/authStore';
import { useIsAdmin, useIsFounder } from '@/hooks/useIsAdmin';
import { useProofsMigration } from '@/hooks/useProofs';
import { cn } from '@/utils/cn';

interface NavLinkSpec {
  to: string;
  labelKey: string;
  icon?: React.ReactNode;
  end?: boolean;
  /** When true, render only when the signed-in user passes
   *  [`useIsFounder`] — strict email-allowlist gate. */
  founderOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: readonly NavLinkSpec[];
  /** Collapsed by default; user must click the header to expand. */
  collapsible?: boolean;
}

const REGULAR_NAV: readonly NavLinkSpec[] = [
  { to: '/',           labelKey: 'nav.home',       icon: <HomeIcon     className="h-4 w-4" aria-hidden />, end: true },
  { to: '/vault',      labelKey: 'nav.vault',      icon: <VaultIcon    className="h-4 w-4" aria-hidden /> },
  { to: '/marketplace',labelKey: 'nav.explore',    icon: <ShoppingBag  className="h-4 w-4" aria-hidden /> },
  { to: '/connectors', labelKey: 'nav.connectors', icon: <Plug         className="h-4 w-4" aria-hidden /> },
  { to: '/integrations', labelKey: 'nav.integrations', icon: <Plug    className="h-4 w-4" aria-hidden /> },
  { to: '/verify',     labelKey: 'nav.verify',     icon: <ShieldAlert  className="h-4 w-4" aria-hidden /> },
  { to: '/settings',   labelKey: 'nav.settings',   icon: <SettingsIcon className="h-4 w-4" aria-hidden /> },
];

const ADVANCED_NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'OMEGA Operations',
    items: [
      { to: '/omega/console',  labelKey: 'nav.omega_console',  founderOnly: true },
      { to: '/omega/briefing', labelKey: 'nav.omega_briefing', founderOnly: true },
      { to: '/omega/activity', labelKey: 'nav.omega_activity', founderOnly: true },
    ],
  },
  {
    label: 'OMEGA Intelligence',
    items: [
      { to: '/omega/launchplans',          labelKey: 'nav.omega_launchplans',          founderOnly: true },
      { to: '/omega/launchplans/insights', labelKey: 'nav.omega_launchplans_insights', founderOnly: true },
      { to: '/omega/decisions',            labelKey: 'nav.omega_decisions',            founderOnly: true },
      { to: '/omega/proposals',            labelKey: 'nav.omega_proposals',            founderOnly: true },
      { to: '/omega/memory',               labelKey: 'nav.omega_memory',               founderOnly: true },
    ],
  },
  {
    label: 'OMEGA Safety',
    items: [
      { to: '/omega/defense',     labelKey: 'nav.omega_defense',     founderOnly: true },
      { to: '/omega/kill-switch', labelKey: 'nav.omega_kill_switch', founderOnly: true },
      { to: '/omega/triggers',    labelKey: 'nav.omega_triggers',    founderOnly: true },
    ],
  },
  {
    label: 'Tools',
    items: [
      // `?key=founder` sets the legacy localStorage flag on first click.
      { to: '/omega?key=founder', labelKey: 'nav.omega',     founderOnly: true },
      { to: '/omega/log',         labelKey: 'nav.omega_log', founderOnly: true },
      { to: '/ceo',               labelKey: 'nav.ceo' },
    ],
  },
  {
    label: 'Legacy',
    collapsible: true,
    items: [
      { to: '/spark',        labelKey: 'nav.spark' },
      { to: '/conductor',    labelKey: 'nav.conductor' },
      { to: '/attestations', labelKey: 'nav.attestations' },
      { to: '/coach',        labelKey: 'nav.coach' },
      { to: '/command',      labelKey: 'nav.command' },
    ],
  },
];

export function RegularUserShell() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const admin = useIsAdmin();
  const isFounder = useIsFounder();
  // Filter each group's items by founder visibility, then drop empty groups.
  const visibleGroups = ADVANCED_NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => !item.founderOnly || isFounder),
  })).filter((g) => g.items.length > 0);

  // Phase: Vault — push any pre-existing localStorage proofs to the
  // server on first authenticated mount. Idempotent across reloads
  // via a per-user `gi-engine.proofs-migrated.{user_id}` flag.
  useProofsMigration();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const advRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Close menus on route change.
  useEffect(() => {
    setDrawerOpen(false);
    setAdvOpen(false);
    setUserOpen(false);
  }, [location.pathname, location.search]);

  // Close pop-ups on outside click + Escape.
  useEffect(() => {
    if (!advOpen && !userOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (advRef.current && !advRef.current.contains(target)) setAdvOpen(false);
      if (userRef.current && !userRef.current.contains(target)) setUserOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAdvOpen(false);
        setUserOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [advOpen, userOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initials = (user?.name ?? '').trim().charAt(0).toUpperCase() || 'U';

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-ink">
      <FounderSignBar />
      <a href="#main" className="skip-to-content">
        {t('skip_to_content', { ns: 'common', defaultValue: 'Skip to content' })}
      </a>
      <OfflineBanner />

      <header className="sticky top-0 z-40 border-b border-border bg-surface/90 backdrop-blur supports-[backdrop-filter]:bg-surface/70">
        <div className="mx-auto flex h-14 w-full max-w-7xl items-center gap-3 px-4 sm:px-6">
          {/* Mobile hamburger (sits on the start edge) */}
          <button
            type="button"
            className="rounded-md p-1.5 text-ink-muted hover:bg-surface-raised md:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label={t('nav.open_menu')}
          >
            <MenuIcon className="h-5 w-5" aria-hidden />
          </button>

          <a
            href="https://growing-intelligence.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-surface-raised"
            aria-label={t('header.brand')}
          >
            <GiOrbitLogo size="sm" />
            <span className="hidden text-sm font-semibold tracking-tight sm:inline">
              {t('header.brand')}
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="ms-4 hidden items-center gap-1 md:flex" aria-label={t('nav.vault')}>
            {REGULAR_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-spark-soft text-spark'
                      : 'text-ink-muted hover:bg-surface-raised hover:text-ink',
                  )
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}

            {admin ? (
              <div className="relative" ref={advRef}>
                <button
                  type="button"
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    advOpen
                      ? 'bg-spark-soft text-spark'
                      : 'text-ink-muted hover:bg-surface-raised hover:text-ink',
                  )}
                  onClick={() => setAdvOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={advOpen}
                >
                  <Sparkles className="h-4 w-4" aria-hidden />
                  {t('nav.advanced_label')}
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </button>
                {advOpen ? (
                  <div
                    role="menu"
                    className="absolute end-0 z-50 mt-2 max-h-[80vh] w-64 overflow-y-auto rounded-lg border border-border bg-surface shadow-float"
                  >
                    {visibleGroups.map((group, gi) => {
                      const isLegacy = !!group.collapsible;
                      return (
                        <div key={group.label} className={gi > 0 ? 'border-t border-border' : ''}>
                          {isLegacy ? (
                            <button
                              type="button"
                              className="flex w-full items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle hover:text-ink"
                              onClick={(e) => { e.stopPropagation(); setLegacyOpen((o) => !o); }}
                            >
                              {group.label}
                              <ChevronDown
                                className={cn('h-3 w-3 shrink-0 transition-transform', legacyOpen && 'rotate-180')}
                                aria-hidden
                              />
                            </button>
                          ) : (
                            <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                              {group.label}
                            </p>
                          )}
                          {(!isLegacy || legacyOpen) &&
                            group.items.map((item) => (
                              <NavLink
                                key={item.to}
                                to={item.to}
                                role="menuitem"
                                className={({ isActive }) =>
                                  cn(
                                    'block px-4 py-1.5 text-sm text-ink hover:bg-surface-raised',
                                    isActive && 'bg-surface-raised font-medium text-spark',
                                  )
                                }
                              >
                                {t(item.labelKey)}
                              </NavLink>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </nav>

          {/* Right cluster */}
          <div className="ms-auto flex items-center gap-2">
            {user ? (
              <div className="relative" ref={userRef}>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md border border-border bg-surface px-2 py-1.5 text-sm hover:bg-surface-raised"
                  onClick={() => setUserOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={userOpen}
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-spark-soft text-xs font-semibold text-spark">
                    {initials}
                  </span>
                  <span className="hidden text-ink-muted sm:inline">{user.name}</span>
                  <ChevronDown className="h-4 w-4 text-ink-subtle" aria-hidden />
                </button>
                {userOpen ? (
                  <div role="menu" className="absolute end-0 mt-2 w-56 overflow-hidden rounded-lg border border-border bg-surface shadow-float">
                    <div className="border-b border-border p-3 text-xs">
                      <p className="font-medium text-ink">{user.name}</p>
                      <p className="truncate text-ink-muted">{user.email}</p>
                    </div>
                    <NavLink
                      to="/settings"
                      role="menuitem"
                      className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-ink hover:bg-surface-raised"
                    >
                      <SettingsIcon className="h-4 w-4" aria-hidden />
                      {t('header.menu.settings')}
                    </NavLink>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-ink hover:bg-surface-raised"
                    >
                      <LogOut className="h-4 w-4" aria-hidden />
                      {t('header.menu.logout')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-label={t('nav.open_menu')}>
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label={t('nav.close_menu')}
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute inset-y-0 start-0 flex w-72 max-w-[80vw] flex-col bg-surface shadow-float">
            <div className="flex h-14 items-center justify-between border-b border-border px-4">
              <div className="flex items-center gap-2">
                <GiOrbitLogo size="sm" />
                <span className="text-sm font-semibold tracking-tight">{t('header.brand')}</span>
              </div>
              <button
                type="button"
                className="rounded-md p-1.5 text-ink-muted hover:bg-surface-raised"
                onClick={() => setDrawerOpen(false)}
                aria-label={t('nav.close_menu')}
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-2" aria-label={t('nav.vault')}>
              {REGULAR_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end ?? false}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
                      isActive ? 'bg-spark-soft text-spark' : 'text-ink hover:bg-surface-raised',
                    )
                  }
                >
                  {item.icon}
                  {t(item.labelKey)}
                </NavLink>
              ))}

              {admin ? (
                <>
                  <p className="mt-3 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
                    {t('nav.advanced_label')}
                  </p>
                  {visibleGroups.map((group) => {
                    const isLegacy = !!group.collapsible;
                    return (
                      <div key={group.label}>
                        {isLegacy ? (
                          <button
                            type="button"
                            className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle hover:text-ink"
                            onClick={() => setLegacyOpen((o) => !o)}
                          >
                            {group.label}
                            <ChevronDown
                              className={cn('h-3 w-3 shrink-0 transition-transform', legacyOpen && 'rotate-180')}
                              aria-hidden
                            />
                          </button>
                        ) : (
                          <p className="px-3 py-1 text-[10px] uppercase tracking-wider text-ink-subtle">
                            {group.label}
                          </p>
                        )}
                        {(!isLegacy || legacyOpen) &&
                          group.items.map((item) => (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              className={({ isActive }) =>
                                cn(
                                  'block rounded-md px-4 py-1.5 text-sm',
                                  isActive ? 'bg-spark-soft font-medium text-spark' : 'text-ink hover:bg-surface-raised',
                                )
                              }
                            >
                              {t(item.labelKey)}
                            </NavLink>
                          ))}
                      </div>
                    );
                  })}
                </>
              ) : null}
            </nav>
            {user ? (
              <div className="border-t border-border p-3">
                <p className="text-xs text-ink-muted">{user.name}</p>
                <p className="truncate text-xs text-ink-subtle">{user.email}</p>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-2 inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-ink hover:bg-surface-raised"
                >
                  <LogOut className="h-4 w-4" aria-hidden />
                  {t('header.menu.logout')}
                </button>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}

      <main id="main" className="flex-1 px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto w-full max-w-7xl">
          <Outlet />
        </div>
      </main>

      <RegularFooter admin={admin} />
      <Toaster />
    </div>
  );
}

function RegularFooter({ admin }: { admin: boolean }) {
  const { t } = useTranslation('dashboard');
  return (
    <footer className="border-t border-border bg-surface/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-ink-muted">{t('footer.tagline')}</p>
          {admin ? (
            <p className="text-xs text-ink-subtle">{t('footer.advanced_desc')}</p>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
